import React from 'react';
import { Link } from 'react-router-dom';
import { Download, FileText, Eye, ArrowRight, CheckCircle2, ShieldCheck, FileSignature } from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { samples, company } from '../mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Samples() {
  return (
    <>
      {/* Hero */}
      <section className="relative pt-32 sm:pt-40 pb-16 sm:pb-20 bg-[#0B1B33] overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
            backgroundSize: '48px 48px',
          }}
        />
        <div className="absolute -bottom-40 -right-32 w-[600px] h-[600px] bg-amber-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-6">
          <div className="text-[11px] sm:text-xs uppercase tracking-[0.2em] text-amber-400 mb-3 sm:mb-4">Sample Deliverables</div>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white tracking-tight leading-[1.08] max-w-3xl">
            See exactly what you'll receive.
          </h1>
          <p className="mt-5 sm:mt-6 text-base sm:text-lg text-slate-300 max-w-2xl leading-relaxed">
            Real-world examples of our cost estimation, material takeoff and quantity takeoff deliverables —
            CSI MasterFormat structured, senior-estimator reviewed, and download-ready.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:gap-4 text-sm text-slate-300">
            <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-amber-400 flex-shrink-0" /> ASPE Class 1–3</span>
            <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-amber-400 flex-shrink-0" /> ACI / NECA / GA-216 compliant</span>
            <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-amber-400 flex-shrink-0" /> Senior-estimator QA</span>
          </div>
        </div>
      </section>

      {/* Samples list */}
      <section className="py-12 sm:py-16 md:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 space-y-8 sm:space-y-10">
          {samples.map((s, idx) => {
            const Icon = s.icon;
            const pdfUrl = `${BACKEND_URL}/api/samples/${s.slug}.pdf`;
            return (
              <Card
                key={s.slug}
                id={s.slug}
                className="bg-white rounded-2xl sm:rounded-3xl border-slate-100 overflow-hidden shadow-sm hover:shadow-xl transition"
              >
                <div className="flex flex-col lg:grid lg:grid-cols-12 lg:gap-0">
                  {/* Dark info card */}
                  <div className="lg:col-span-5 bg-[#0B1B33] p-5 sm:p-7 md:p-9 text-white relative overflow-hidden">
                    <div className="absolute -top-20 -right-20 w-48 h-48 bg-amber-500/20 rounded-full blur-3xl pointer-events-none" />
                    <div className="relative">
                      <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                        <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center flex-shrink-0">
                          <Icon className="w-5 h-5 text-[#0B1B33]" />
                        </div>
                        <Badge className="bg-white/10 text-amber-400 hover:bg-white/10 border border-amber-400/20 text-[10px] sm:text-xs">
                          {s.category}
                        </Badge>
                      </div>
                      <h2 className="mt-4 sm:mt-5 text-xl sm:text-2xl font-bold leading-tight tracking-tight">
                        {s.title}
                      </h2>
                      <p className="mt-2 sm:mt-3 text-sm text-slate-300 leading-relaxed">{s.summary}</p>

                      <div className="mt-5 sm:mt-6 grid grid-cols-2 gap-2 sm:gap-3">
                        {s.keyMetrics.map((k, i) => (
                          <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-2.5 sm:p-3">
                            <div className="text-[9px] sm:text-[10px] uppercase tracking-wider text-slate-400 leading-tight">{k.label}</div>
                            <div className="mt-0.5 text-amber-400 font-bold text-sm sm:text-base">{k.value}</div>
                          </div>
                        ))}
                      </div>

                      <div className="mt-6 flex flex-col sm:flex-row gap-2.5">
                        <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className="flex-1">
                          <Button className="w-full bg-amber-500 hover:bg-amber-600 text-[#0B1B33] font-semibold rounded-lg h-11">
                            <Eye className="w-4 h-4 mr-2" /> Preview PDF
                          </Button>
                        </a>
                        <a href={pdfUrl} download className="flex-1">
                          <Button variant="outline" className="w-full border-white/20 bg-white/5 text-white hover:bg-white/10 hover:text-amber-400 rounded-lg h-11">
                            <Download className="w-4 h-4 mr-2" /> Download
                          </Button>
                        </a>
                      </div>
                      <p className="mt-3 text-[11px] text-slate-400 leading-tight">
                        PDF · prepared by Core Estimation LLC · numbers illustrative
                      </p>
                    </div>
                  </div>

                  {/* Table excerpt */}
                  <div className="lg:col-span-7 p-5 sm:p-7 md:p-9">
                    <div className="flex items-center justify-between mb-4 sm:mb-5">
                      <h3 className="text-[11px] sm:text-xs uppercase tracking-wider font-semibold text-slate-500">
                        Sample #{String(idx + 1).padStart(2, '0')} · Excerpt
                      </h3>
                      <FileText className="w-5 h-5 text-amber-500" />
                    </div>
                    <div className="overflow-x-auto rounded-xl border border-slate-100 -mx-1 sm:mx-0">
                      <table className="w-full text-[13px] sm:text-sm min-w-[420px]">
                        <thead className="bg-[#0B1B33] text-white">
                          <tr>
                            {s.columns.map((c, i) => (
                              <th
                                key={i}
                                className={`px-2.5 sm:px-3 md:px-4 py-2.5 font-semibold text-[10px] sm:text-[11px] uppercase tracking-wider whitespace-nowrap ${
                                  i >= 2 ? 'text-right' : 'text-left'
                                }`}
                              >
                                {c}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {s.rows.map((row, i) => (
                            <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50/60'}>
                              {row.map((cell, j) => (
                                <td
                                  key={j}
                                  className={`px-2.5 sm:px-3 md:px-4 py-2.5 text-slate-700 align-top ${
                                    j >= 2 ? 'text-right font-medium whitespace-nowrap' : ''
                                  } ${j === 0 ? 'font-mono text-[11px] sm:text-xs text-slate-500 whitespace-nowrap' : ''}`}
                                >
                                  {cell}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="mt-4 sm:mt-5 flex items-start gap-2 text-[11px] sm:text-xs text-slate-500 leading-snug">
                      <ShieldCheck className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                      <span>
                        Full deliverable includes CSI MasterFormat breakdown, inclusions / exclusions, OH&amp;P math,
                        and drawing-referenced line items.
                      </span>
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </section>

      {/* Proposal / Quote Template */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-6">
          <div className="grid lg:grid-cols-12 gap-6 lg:gap-10 items-stretch bg-gradient-to-br from-slate-50 to-amber-50/40 rounded-2xl sm:rounded-3xl border border-amber-100 overflow-hidden">
            <div className="lg:col-span-7 p-6 sm:p-10 md:p-12">
              <div className="text-[11px] sm:text-xs uppercase tracking-[0.2em] text-amber-600 font-semibold mb-3">
                Tools &amp; Templates
              </div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#0B1B33] tracking-tight leading-tight">
                The Estimating Proposal Template we send to every client.
              </h2>
              <p className="mt-3 sm:mt-4 text-slate-600 text-sm sm:text-base leading-relaxed">
                A ready-to-fill, professional fixed-fee proposal PDF you can send to a client right after they
                share drawings. Branded, signature-ready, and pre-loaded with the scope, inclusions, exclusions
                and terms a clean estimating engagement needs.
              </p>
              <ul className="mt-5 grid sm:grid-cols-2 gap-2.5 text-sm text-slate-700">
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" /> Fillable project &amp; client fields</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" /> Highlighted fixed-fee &amp; turnaround</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" /> Inclusions, exclusions &amp; assumptions</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" /> Terms, validity &amp; signature block</li>
              </ul>
              <div className="mt-7 flex flex-col sm:flex-row gap-3 flex-wrap">
                <a
                  href={`${BACKEND_URL}/api/samples/quote-template.pdf`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full sm:w-auto"
                  data-testid="quote-template-preview"
                >
                  <Button size="lg" variant="outline" className="w-full border-[#0B1B33]/20 bg-white text-[#0B1B33] hover:bg-amber-50 hover:border-amber-500 rounded-xl h-12 px-6">
                    <Eye className="w-4 h-4 mr-2" /> Preview Blank PDF
                  </Button>
                </a>
                <a
                  href={`${BACKEND_URL}/api/samples/quote-template.pdf`}
                  download
                  className="w-full sm:w-auto"
                  data-testid="quote-template-download"
                >
                  <Button size="lg" variant="outline" className="w-full border-[#0B1B33]/20 bg-white text-[#0B1B33] hover:bg-amber-50 hover:border-amber-500 rounded-xl h-12 px-6">
                    <Download className="w-4 h-4 mr-2" /> Download Blank PDF
                  </Button>
                </a>
              </div>
            </div>

            <div className="lg:col-span-5 bg-[#0B1B33] p-6 sm:p-10 md:p-12 text-white relative overflow-hidden flex flex-col justify-center">
              <div className="absolute -top-24 -right-24 w-72 h-72 bg-amber-500/20 rounded-full blur-3xl pointer-events-none" />
              <div className="relative">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center mb-5">
                  <FileSignature className="w-6 h-6 text-[#0B1B33]" />
                </div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-amber-400 mb-2 font-semibold">
                  Inside the Template
                </div>
                <ul className="space-y-2.5 text-sm text-slate-200">
                  <li className="flex items-start gap-2"><span className="text-amber-400 font-bold">01</span> Cover letter &amp; proposal summary card</li>
                  <li className="flex items-start gap-2"><span className="text-amber-400 font-bold">02</span> Project information table</li>
                  <li className="flex items-start gap-2"><span className="text-amber-400 font-bold">03</span> Scope of services</li>
                  <li className="flex items-start gap-2"><span className="text-amber-400 font-bold">04</span> Fixed fee &amp; turnaround</li>
                  <li className="flex items-start gap-2"><span className="text-amber-400 font-bold">05</span> Inclusions / Exclusions / Assumptions</li>
                  <li className="flex items-start gap-2"><span className="text-amber-400 font-bold">06</span> Terms &amp; conditions</li>
                  <li className="flex items-start gap-2"><span className="text-amber-400 font-bold">07</span> Dual signature block</li>
                </ul>
                <div className="mt-6 pt-5 border-t border-white/10 text-[11px] text-slate-400">
                  3-page PDF · branded · Letter-size · senior-estimator approved
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-6">
          <div className="bg-[#0B1B33] rounded-2xl sm:rounded-3xl p-7 sm:p-10 md:p-14 grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white tracking-tight leading-tight">
                Like what you see? Send us your drawings.
              </h2>
              <p className="mt-3 sm:mt-4 text-slate-300 text-sm sm:text-base">
                We'll deliver an estimate or takeoff in the same caliber — within 48 hours, fixed fee.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 lg:justify-end">
              <Link to="/contact" className="w-full sm:w-auto">
                <Button size="lg" className="bg-amber-500 hover:bg-amber-600 text-[#0B1B33] font-semibold h-12 px-7 rounded-xl w-full">
                  Request a Quote <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <a href={`tel:${company.phoneRaw}`} className="w-full sm:w-auto">
                <Button size="lg" variant="outline" className="border-white/30 bg-transparent text-white hover:bg-white hover:text-[#0B1B33] h-12 px-7 rounded-xl w-full">
                  Call {company.phone}
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
